import React, { createContext, useContext, useMemo } from 'react'
import { TicketsStatusReference } from '@/types/codegen-federation'
import { Downloadable } from '../DownloadablesService'
import { PlayableLivestream } from '../LivestreamService'
import { Note } from '../StoriesService/types'
import { CurrentLivestream, VideoMetaData, WatchableNode } from './types'

interface WatchProjectContextValue {
  bonusVideos: VideoMetaData[]
  livestreams: PlayableLivestream[]
  downloadables: Downloadable[]
  communityStories: Note[]
  watchables: WatchableNode[]
  hasLivestreams: boolean
  hasDownloadables: boolean
  hasMerchItems: boolean
  hasBonusVideos: boolean
  currentLivestream: CurrentLivestream | null
  ticketStatus: TicketsStatusReference | null
}

interface WatchProjectProviderProps {
  children: React.ReactNode
  bonusVideos: VideoMetaData[]
  livestreams: PlayableLivestream[]
  downloadables: Downloadable[]
  communityStories: Note[]
  watchables: WatchableNode[]
  hasLivestreams: boolean
  hasDownloadables: boolean
  hasMerchItems: boolean
  hasBonusVideos: boolean
  currentLivestream: CurrentLivestream | null
  ticketStatus: TicketsStatusReference | null
}

const WatchProjectContext = createContext<WatchProjectContextValue | undefined>(undefined)

export const WatchProjectProvider: React.FC<WatchProjectProviderProps> = ({
  children,
  bonusVideos,
  livestreams,
  downloadables,
  communityStories,
  watchables,
  hasLivestreams,
  hasDownloadables,
  hasMerchItems,
  hasBonusVideos,
  currentLivestream,
  ticketStatus,
}) => {
  const value = useMemo(
    () => ({
      bonusVideos,
      livestreams,
      downloadables,
      communityStories,
      watchables,
      hasLivestreams,
      hasDownloadables,
      hasMerchItems,
      hasBonusVideos,
      currentLivestream,
      ticketStatus,
    }),
    [
      bonusVideos,
      livestreams,
      downloadables,
      communityStories,
      watchables,
      hasLivestreams,
      hasDownloadables,
      hasMerchItems,
      hasBonusVideos,
      currentLivestream,
      ticketStatus,
    ],
  )

  return <WatchProjectContext.Provider value={value}>{children}</WatchProjectContext.Provider>
}

export const useWatchProjectContext = () => {
  const context = useContext(WatchProjectContext)
  if (!context) {
    throw new Error('useWatchProjectContext must be used within a WatchProjectProvider')
  }
  return context
}
